import { keccak256, type Hex } from 'viem';
import { privateKeyToAccount, type PrivateKeyAccount } from 'viem/accounts';

const PRF_SALT = new TextEncoder().encode('cohort-signing-key-v1');
const STORAGE_KEY = 'cohort_passkey_address';

// Cached signing account (in-memory only for security)
let cachedAccount: PrivateKeyAccount | null = null;
let cachedAddress: Hex | null = null;

/**
 * Create a new passkey with PRF support
 * Uses discoverable credentials - no need to store credential ID
 */
export async function createPasskey(name: string): Promise<Hex> {
  await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: {
        name: 'Cohort App',
        id: window.location.hostname
      },
      user: {
        id: crypto.getRandomValues(new Uint8Array(16)),
        name: name,
        displayName: name
      },
      pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
      authenticatorSelection: {
        residentKey: 'required',  // Discoverable - browser remembers it
        userVerification: 'preferred'
      },
      extensions: { prf: {} }
    }
  }) as PublicKeyCredential;

  // Verify PRF worked and return the derived address
  const { address } = await getSigningKey();
  return address;
}

/**
 * Derive secp256k1 signing key from passkey via PRF
 * Browser shows picker for all passkeys registered to this site
 * Results are cached - passkey prompt only happens once per session
 */
export async function getSigningKey(): Promise<{
  privateKey: Hex;
  account: PrivateKeyAccount;
  address: Hex;
}> {
  // Return cached account if available
  if (cachedAccount && cachedAddress) {
    return {
      privateKey: '0x' as Hex, // Don't expose private key after initial derivation
      account: cachedAccount,
      address: cachedAddress
    };
  }

  // No allowCredentials = browser shows picker of all passkeys for this site
  const credential = await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rpId: window.location.hostname,
      userVerification: 'preferred',
      extensions: {
        prf: {
          eval: { first: PRF_SALT }
        }
      }
      // No allowCredentials! Browser shows all available passkeys
    }
  }) as PublicKeyCredential;

  const prfResult = (credential.getClientExtensionResults() as any).prf;

  if (!prfResult?.results?.first) {
    throw new Error('PRF extension not supported by this authenticator');
  }

  const prfOutput = new Uint8Array(prfResult.results.first);
  const privateKey = keccak256(prfOutput);
  const account = privateKeyToAccount(privateKey);

  // Cache for subsequent signatures (no more passkey prompts this session)
  cachedAccount = account;
  cachedAddress = account.address;

  // Also persist address to localStorage for refresh persistence
  localStorage.setItem(STORAGE_KEY, account.address);

  return {
    privateKey,
    account,
    address: account.address
  };
}

/**
 * Get cached address from localStorage (for refresh persistence)
 * Note: This only returns the address, not the signing capability.
 * User must re-authenticate with passkey to sign transactions.
 */
export function getCachedAddress(): Hex | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored.startsWith('0x')) {
    cachedAddress = stored as Hex;
    return stored as Hex;
  }
  return null;
}

/**
 * Check if we have a cached signing account (can sign without prompting)
 */
export function hasSigningCapability(): boolean {
  return cachedAccount !== null;
}

/**
 * Clear cached credentials (logout)
 */
export function clearCachedCredentials(): void {
  cachedAccount = null;
  cachedAddress = null;
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Check if PRF is likely supported (heuristic)
 */
export function isPRFLikelySupported(): boolean {
  return 'credentials' in navigator &&
         'create' in navigator.credentials &&
         /Chrome\/(\d+)/.test(navigator.userAgent) &&
         parseInt(RegExp.$1) >= 116;
}
